import Image from 'next/image';
import Link from 'next/link';
import { NextSeo } from 'next-seo';

import Footer from '@/components/layouts/Footer';
import Header from '@/components/layouts/Header';
import PageLink from '@/utils/PageLink';

const useCases = [
  {
    title: 'Make every first visit easier',
    description:
      'Help newcomers find service times, parking, accessibility information, and what to expect before they walk through your doors.',
    examples: [
      'Service times & locations',
      'Children’s programs & check-in',
      'Directions & accessibility',
    ],
    path: 'M3 10l9-7 9 7M5 9v11h14V9M9 20v-7h6v7',
  },
  {
    title: 'Help people find their community',
    description:
      'Make it easier to discover small groups, classes, ministry gatherings, and upcoming events using the information your team provides.',
    examples: ['Small groups & study guides', 'Events & registration links', 'Ministry contacts'],
    path: 'M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2M16 3a4 4 0 010 8M22 21v-2a4 4 0 00-3-3.87M13 7a4 4 0 11-8 0 4 4 0 018 0z',
  },
  {
    title: 'Equip your volunteers',
    description:
      'Give volunteers a clear place to find role descriptions, orientation materials, and service guidelines, so leaders spend less time repeating instructions.',
    examples: [
      'Volunteer opportunities',
      'Training & team handbooks',
      'Serving requirements & next steps',
    ],
    path: 'M9 12l2 2 4-4M9 5H5a2 2 0 00-2 2v12a2 2 0 002 2h14a2 2 0 002-2V7a2 2 0 00-2-2h-4M9 3h6v4H9z',
  },
  {
    title: 'Put your teachings within reach',
    description:
      'Help your community explore uploaded sermon transcripts, study materials, and statements of belief, with citations back to the sources you choose.',
    examples: [
      'Sermon transcripts & notes',
      'Faith formation resources',
      'Your organization’s beliefs & mission',
    ],
    path: 'M12 7v14m0-14C9 5 5 5 2 6v14c3-1 7-1 10 1m0-14c3-2 7-2 10-1v14c-3-1-7-1-10 1',
  },
  {
    title: 'Connect neighbors with support',
    description:
      'Make outreach information easier to find, from food pantry hours to assistance guidelines and the right person to contact.',
    examples: [
      'Outreach programs & hours',
      'Eligibility & application guidance',
      'Community resource information',
    ],
    path: 'M20.84 4.61a5.5 5.5 0 00-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 00-7.78 7.78L12 21.23l8.84-8.84a5.5 5.5 0 000-7.78z',
  },
  {
    title: 'Make office information easy to find',
    description:
      'Answer routine questions about giving, facility use, and office procedures from approved materials, and point people to the appropriate forms or staff.',
    examples: [
      'Giving information & approved links',
      'Facility policies & request forms',
      'Office hours & staff contacts',
    ],
    path: 'M8 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2h-3M9 3h6v4H9zM7 12h10M7 16h7',
  },
];

const administrationResources = [
  {
    title: 'HR policies & staff handbooks',
    description:
      'Help staff find leave policies, benefits information, onboarding steps, and workplace procedures in the handbooks your organization provides.',
    question: 'What is our process for requesting time off?',
    source: 'Employee handbook',
  },
  {
    title: 'Board minutes & governance',
    description:
      'Give authorized leaders a way to look up recorded decisions, resolutions, bylaws, and committee responsibilities, preserving context as leadership changes.',
    question: 'What did the board approve about the building project?',
    source: 'Board meeting minutes',
  },
  {
    title: 'Contracts & vendor agreements',
    description:
      'Find renewal terms, notice periods, service commitments, and vendor responsibilities in uploaded contracts, with citations so your team can check the original language.',
    question: 'What notice does our maintenance contract require for renewal?',
    source: 'Facilities service agreement',
  },
  {
    title: 'Administration & operations',
    description:
      'Bring purchasing procedures, expense policies, facilities information, and staff responsibilities within reach, so institutional knowledge stays accessible through transitions.',
    question: 'Who approves a purchase, and which form should I use?',
    source: 'Administrative procedures',
  },
];

const steps = [
  {
    title: 'Share your knowledge',
    description:
      'Choose a starting point: community information or internal administration. Add the website pages, handbooks, minutes, or other documents that assistant should use.',
  },
  {
    title: 'Make it your own',
    description:
      'Set your assistant’s tone and access, choose its sources, and test answers with your team before sharing it.',
  },
  {
    title: 'Put knowledge to work',
    description:
      'Embed your community assistant on your website, or share a private assistant with approved staff and leaders. Keep its source documents current as policies and programs change.',
  },
];

const faqs = [
  {
    question: 'Can we use Chat.co just for internal church administration?',
    answer:
      'Yes. Start with a private assistant for your staff or leadership team. Provide HR policies, board meeting minutes, bylaws, administrative procedures, and contracts to help authorized users find answers with citations to the source documents. A public website assistant is optional.',
  },
  {
    question: 'Is this only for churches?',
    answer:
      'No. Churches, ministries, faith-based nonprofits, and other religious communities can build an assistant around their own mission, resources, and practices. You choose the materials and language that represent your organization.',
  },
  {
    question: 'Can answers reflect our beliefs and teachings?',
    answer:
      'You can provide your statement of faith, approved teaching materials, and sermon transcripts as sources, and configure the assistant’s instructions and tone. Review its answers before launch and keep the source materials up to date.',
  },
  {
    question: 'Does the assistant replace pastoral care?',
    answer:
      'The assistant helps with information and everyday questions. Pastoral care and personal spiritual guidance remain with your people. Include clear contact details and instructions so visitors know how to reach a pastor, ministry leader, or care team.',
  },
  {
    question: 'Can we have separate public and staff resources?',
    answer:
      'Yes. Use a public assistant for visitor information and separate private assistants with email access controls for staff or board resources. Give each assistant only the documents its approved audience should be able to read. For example, keep board-only minutes in a leadership assistant rather than a general staff assistant.',
  },
  {
    question: 'Do we need a technical team to get started?',
    answer:
      'No coding is needed to create and configure your assistant. Upload your materials, test a few common questions, and share its direct link. You can also add a chat widget to your website with a small embed snippet.',
  },
];

const primaryButton =
  'inline-flex items-center justify-center rounded-full bg-primary-600 px-8 py-3.5 text-base font-semibold text-white transition-colors hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600';
const secondaryButton =
  'inline-flex items-center justify-center rounded-full border border-gray-300 bg-white px-8 py-3.5 text-base font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-primary-600';

const FaithBasedUseCase = () => (
  <div>
    <NextSeo
      title="AI for Faith-Based Organizations & Churches"
      description="AI for church communities and internal administration. Find answers in HR policies, board minutes, and contracts, and help visitors and volunteers access trusted resources."
      canonical="https://chat.co/use-cases/faith-based"
      openGraph={{
        url: 'https://chat.co/use-cases/faith-based',
        images: [
          {
            url: 'https://chat.co/images/faith-based/community-welcome.jpg',
            width: 1536,
            height: 1024,
            alt: 'A welcoming church community',
          },
        ],
      }}
    />
    <Header />
    <main>
      <section className="overflow-hidden bg-gradient-to-br from-rose-50 via-orange-50/60 to-white px-4 pb-16 pt-10 sm:px-6 lg:px-8 lg:pb-20">
        <div className="mx-auto max-w-7xl">
          <Link
            href="/use-cases/nonprofit"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-primary-700">
            <span aria-hidden="true">←</span> Non-Profit & Associations
          </Link>
          <div className="mt-10 grid items-center gap-10 lg:grid-cols-2 lg:gap-14">
            <div>
              <span className="inline-flex rounded-full bg-rose-100 px-4 py-2 text-sm font-semibold text-rose-800">
                Faith-Based Organizations & Churches
              </span>
              <h1 className="mt-6 text-4xl font-bold leading-tight tracking-tight text-gray-900 sm:text-5xl lg:text-6xl">
                More time for people.
                <br />
                <span className="text-primary-600">More room for your mission.</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg leading-8 text-gray-600">
                Put your church’s knowledge to work, from welcoming visitors to supporting the
                people who run your organization. Help your community find answers and give staff
                and leaders private access to HR policies, board minutes, contracts, and everyday
                administrative guidance.
              </p>
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link href={PageLink.demo} className={primaryButton}>
                  Schedule a Demo{' '}
                  <span className="ml-2" aria-hidden="true">
                    →
                  </span>
                </Link>
                <Link href={PageLink.login} className={secondaryButton}>
                  Start Free Trial
                </Link>
              </div>
              <p className="mt-5 text-sm text-gray-500">
                Your knowledge. Your voice. A helping hand, around the clock.
              </p>
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-3 text-sm font-semibold text-primary-700">
                <a href="#internal-administration" className="hover:underline">
                  For staff & leadership <span aria-hidden="true">↓</span>
                </a>
                <a href="#community-support" className="hover:underline">
                  For your community <span aria-hidden="true">↓</span>
                </a>
              </div>
            </div>
            <div className="relative pb-8">
              <div className="relative aspect-[4/3] overflow-hidden rounded-3xl shadow-xl lg:aspect-[6/5]">
                <Image
                  src="/images/faith-based/community-welcome.jpg"
                  alt="Community members warmly greeting one another in a sunlit church entrance"
                  fill
                  priority
                  sizes="(max-width: 1023px) 100vw, 50vw"
                  className="object-cover"
                />
              </div>
              <div className="relative mx-4 -mt-12 flex items-center gap-4 rounded-2xl border border-rose-100 bg-white p-5 shadow-lg sm:mx-8">
                <span
                  className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-rose-50 text-rose-700"
                  aria-hidden="true">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8">
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 11.5a8.4 8.4 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.4 8.4 0 01-3.8-.9L3 21l1.9-5.7a8.4 8.4 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.4 8.4 0 013.8-.9h.5a8.5 8.5 0 018 8v.5z"
                    />
                  </svg>
                </span>
                <div>
                  <p className="font-semibold text-gray-900">
                    A warm welcome starts with an answer.
                  </p>
                  <p className="mt-1 text-sm text-gray-500">
                    Before the first visit. Beyond office hours.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section
        aria-label="Benefits for your organization"
        className="border-y border-gray-100 bg-white px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto grid max-w-7xl gap-7 sm:grid-cols-3">
          {[
            ['Available 24/7', 'Help people find information on their schedule.'],
            ['Grounded in your sources', 'Connect answers to the materials you provide.'],
            ['Public or private', 'Dedicated assistants for your community, staff, and leaders.'],
          ].map(([title, description]) => (
            <div key={title} className="text-center">
              <h2 className="text-base font-semibold text-gray-900">{title}</h2>
              <p className="mt-2 text-sm text-gray-600">{description}</p>
            </div>
          ))}
        </div>
      </section>

      <section
        id="internal-administration"
        className="scroll-mt-24 bg-gradient-to-b from-primary-50/60 to-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-widest text-primary-700">
              For staff, administrators & leadership
            </p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Your church’s institutional knowledge. Ready when your team needs it.
            </h2>
            <p className="mt-5 text-lg leading-8 text-gray-600">
              Policies live in binders. Decisions live in meeting minutes. Contract details live in
              someone’s inbox. Bring the documents your team relies on into a private knowledge
              assistant, so staff and leaders can ask a question and trace the answer to its source.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2">
            {administrationResources.map((resource) => (
              <article
                key={resource.title}
                className="flex flex-col rounded-2xl border border-primary-100 bg-white p-7 sm:p-8">
                <h3 className="text-xl font-semibold text-gray-900">{resource.title}</h3>
                <p className="mb-6 mt-3 leading-7 text-gray-600">{resource.description}</p>
                <div className="mt-auto rounded-xl bg-gray-50 p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-primary-700">
                    A question your team could ask
                  </p>
                  <p className="mt-3 font-medium leading-7 text-gray-900">{resource.question}</p>
                  <p className="mt-3 text-sm text-gray-500">Source to provide: {resource.source}</p>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-8 rounded-2xl border border-primary-100 bg-primary-50 p-7 sm:p-8">
            <h3 className="text-xl font-semibold text-gray-900">
              The right knowledge for the right people.
            </h3>
            <p className="mt-3 max-w-4xl leading-7 text-gray-600">
              Create separate private assistants for staff and board members, with email access
              controls for each audience. Include only the documents that audience is authorized to
              read. Keep your public community assistant focused on information everyone can access.
            </p>
            <p className="mt-4 font-medium text-primary-800">
              Start with internal administration, community support, or both.
            </p>
          </div>
        </div>
      </section>

      <section
        id="community-support"
        className="scroll-mt-24 bg-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="mx-auto max-w-3xl text-center">
            <p className="text-sm font-semibold uppercase tracking-widest text-rose-700">
              For visitors, members & volunteers
            </p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Help your community take the next step.
            </h2>
            <p className="mt-5 text-lg leading-8 text-gray-600">
              Small teams carry a lot. Help people find the information they need without adding
              another call or email to your day.
            </p>
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {useCases.map((useCase) => (
              <article
                key={useCase.title}
                className="rounded-2xl border border-gray-200 bg-white p-7">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-rose-50 text-rose-700">
                  <svg
                    aria-hidden="true"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d={useCase.path} />
                  </svg>
                </div>
                <h3 className="mt-5 text-xl font-semibold text-gray-900">{useCase.title}</h3>
                <p className="mt-3 text-base leading-7 text-gray-600">{useCase.description}</p>
                <ul className="mt-5 space-y-3">
                  {useCase.examples.map((example) => (
                    <li key={example} className="flex gap-2 text-sm leading-6 text-gray-600">
                      <span className="text-rose-600" aria-hidden="true">
                        ✓
                      </span>
                      {example}
                    </li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-rose-50 to-orange-50/50 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="relative aspect-[4/3] overflow-hidden rounded-3xl shadow-lg">
            <Image
              src="/images/faith-based/community-service.jpg"
              alt="Volunteers packing fresh food and pantry supplies for a community outreach program"
              fill
              sizes="(max-width: 1023px) 100vw, 50vw"
              className="object-cover"
            />
          </div>
          <div>
            <p className="text-sm font-semibold uppercase tracking-widest text-rose-700">
              Technology in service of people
            </p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              Keep the personal connection at the center.
            </h2>
            <p className="mt-5 text-lg leading-8 text-gray-600">
              Your people make your ministry. Chat.co gives them a little more room to do what they
              do best: welcome, listen, teach, and serve.
            </p>
            <div className="mt-7 space-y-6">
              <div>
                <h3 className="font-semibold text-gray-900">Answers rooted in your resources</h3>
                <p className="mt-2 leading-7 text-gray-600">
                  Share approved guides, teachings, and program details. Citations help people see
                  where an answer comes from and explore further.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">A clear path to your team</h3>
                <p className="mt-2 leading-7 text-gray-600">
                  Point people to the right ministry contact when a question needs a conversation.
                  Pastoral care and spiritual guidance stay with your leaders.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <div className="text-center">
            <p className="text-sm font-semibold uppercase tracking-widest text-rose-700">
              A simple place to start
            </p>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              From your resources to ready-to-help.
            </h2>
          </div>
          <div className="mt-12 grid gap-10 md:grid-cols-3">
            {steps.map((step, index) => (
              <div key={step.title}>
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-primary-50 text-lg font-semibold text-primary-700">
                  0{index + 1}
                </span>
                <h3 className="mt-5 text-xl font-semibold text-gray-900">{step.title}</h3>
                <p className="mt-3 leading-7 text-gray-600">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gray-50 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-center text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Questions from ministry teams
          </h2>
          <div className="mt-10 space-y-3">
            {faqs.map((faq) => (
              <details
                key={faq.question}
                className="group rounded-2xl border border-gray-200 bg-white px-6 py-5">
                <summary className="cursor-pointer text-base font-semibold text-gray-900 focus-visible:outline-primary-600">
                  {faq.question}
                </summary>
                <p className="mt-4 leading-7 text-gray-600">{faq.answer}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-primary-700 to-primary-900 px-4 py-16 sm:px-6 sm:py-20 lg:px-8">
        <div className="mx-auto max-w-3xl text-center">
          <p className="text-sm font-semibold uppercase tracking-widest text-primary-100">
            Your mission. A little more supported.
          </p>
          <h2 className="mt-5 text-3xl font-bold tracking-tight text-white sm:text-4xl">
            Be there for your community.
            <br />
            Even between gatherings.
          </h2>
          <p className="mx-auto mt-5 max-w-2xl text-lg leading-8 text-primary-100">
            Start with the questions your team answers every week, from staff policies and board
            decisions to service times and volunteer opportunities. Make your knowledge a resource
            for the people who serve and the people you serve.
          </p>
          <div className="mt-8 flex flex-col justify-center gap-4 sm:flex-row">
            <Link
              href={PageLink.demo}
              className="inline-flex items-center justify-center rounded-full bg-white px-8 py-3.5 font-semibold text-primary-800 transition-colors hover:bg-primary-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
              Schedule a Demo{' '}
              <span className="ml-2" aria-hidden="true">
                →
              </span>
            </Link>
            <Link
              href={PageLink.login}
              className="inline-flex items-center justify-center rounded-full border border-white/60 px-8 py-3.5 font-semibold text-white transition-colors hover:bg-white/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white">
              Start Free Trial
            </Link>
          </div>
        </div>
      </section>
    </main>
    <Footer />
  </div>
);

export const getServerSideProps = async () => ({ props: {} });

export default FaithBasedUseCase;
